import type { ApiResponse } from "@/types/common";
import { apiClient, unwrapApiResponse } from "./httpClient";

export type ChannelLevel = "Distributor" | "Wholesaler" | "Retailer";

export interface ChannelParty {
  id: number;
  userId: string;
  level: ChannelLevel | number;
  levelText: string;
  parentPartyId?: number;
  parentBusinessName?: string;
  storeId?: number;
  businessName: string;
  contactName: string;
  phone: string;
  email?: string;
  city?: string;
  status: number;
  statusText: string;
  createdAt: string;
}

export interface ChannelPartyCreate {
  level: ChannelLevel;
  businessName: string;
  contactName: string;
  phone: string;
  email: string;
  password: string;
  city?: string;
  parentPartyId?: number;
  storeId?: number;
}

export type ChannelPartyStatusText = "Pending" | "Active" | "Suspended";

export interface ChannelPartyUpdate {
  businessName: string;
  contactName: string;
  phone: string;
  city?: string;
  status: number;
}

export const channelPartyStatusNumber: Record<ChannelPartyStatusText, number> = {
  Pending: 0,
  Active: 1,
  Suspended: 2,
};

const levelNumber: Record<ChannelLevel, number> = {
  Distributor: 1,
  Wholesaler: 2,
  Retailer: 3,
};

export const channelPartiesService = {
  getAll(level?: ChannelLevel): Promise<ChannelParty[]> {
    return unwrapApiResponse(
      apiClient.get<ApiResponse<ChannelParty[]>>("/ChannelParties", {
        params: level ? { level: levelNumber[level] } : undefined,
      }),
    );
  },

  create(dto: ChannelPartyCreate): Promise<ChannelParty> {
    return unwrapApiResponse(
      apiClient.post<ApiResponse<ChannelParty>>("/ChannelParties", {
        ...dto,
        level: levelNumber[dto.level],
      }),
    );
  },

  update(id: number, dto: ChannelPartyUpdate): Promise<ChannelParty> {
    return unwrapApiResponse(apiClient.put<ApiResponse<ChannelParty>>(`/ChannelParties/${id}`, dto));
  },

  remove(id: number): Promise<boolean> {
    return unwrapApiResponse(apiClient.delete<ApiResponse<boolean>>(`/ChannelParties/${id}`));
  },

  syncRetailers(): Promise<number> {
    return unwrapApiResponse(apiClient.post<ApiResponse<number>>("/ChannelParties/sync-retailers"));
  },

  getMine(): Promise<ChannelParty> {
    return unwrapApiResponse(apiClient.get<ApiResponse<ChannelParty>>("/ChannelParties/me"));
  },
};
